import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Plus, Trash2, GripVertical, ExternalLink, Copy, Globe,
  Eye, EyeOff, Pencil, Check, Link2, Sparkles,
} from "lucide-react";

const ICON_OPTIONS = ["🔗","📱","🌐","📧","🎵","🎬","📸","💼","🛒","📝","🎨","🎤","📰","🏆","❤️"];

interface BioItem { id: string; title: string; url: string; icon: string; position: number; is_active: boolean; click_count: number; }
interface BioPage {
  id: string; title: string; slug: string; bio: string; avatar_url: string;
  bg_color: string; text_color: string; button_color: string; is_published: boolean; view_count: number;
}

export function LinkInBioBuilder() {
  const { orgId } = useOrgPermissions();
  const qc = useQueryClient();
  const { toast } = useToast();

  const [saving, setSaving]         = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [newItem, setNewItem]       = useState({ title: "", url: "", icon: "🔗" });
  const [editPage, setEditPage]     = useState<Partial<BioPage>>({});
  const [preview, setPreview]       = useState(false);

  const { data: page, refetch: refetchPage } = useQuery({
    queryKey: ["link-in-bio-page", orgId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await (supabase as any)
        .from("link_in_bio_pages")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data) setEditPage(data);
      return data as BioPage | null;
    },
  });

  const { data: items = [], refetch: refetchItems } = useQuery({
    queryKey: ["link-in-bio-items", page?.id],
    enabled: !!page?.id,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("link_in_bio_items")
        .select("*")
        .eq("page_id", page!.id)
        .order("position");
      return (data ?? []) as BioItem[];
    },
  });

  const handleCreatePage = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const slug = `${user.id.slice(0, 8)}-${Date.now().toString(36)}`;
    const { error } = await (supabase as any).from("link_in_bio_pages").insert({
      user_id: user.id,
      org_id: orgId,
      title: "My Links",
      slug,
      bio: "",
      bg_color: "#0D2240",
      text_color: "#ffffff",
      button_color: "#006B3C",
    });
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    refetchPage();
  };

  const handleSavePage = async () => {
    if (!page) return;
    setSaving(true);
    await (supabase as any).from("link_in_bio_pages").update({
      title: editPage.title ?? page.title,
      bio: editPage.bio ?? page.bio,
      bg_color: editPage.bg_color ?? page.bg_color,
      text_color: editPage.text_color ?? page.text_color,
      button_color: editPage.button_color ?? page.button_color,
      is_published: editPage.is_published ?? page.is_published,
      updated_at: new Date().toISOString(),
    }).eq("id", page.id);
    setSaving(false);
    refetchPage();
    toast({ title: "Page saved" });
  };

  const handleAddItem = async () => {
    if (!page || !newItem.title.trim() || !newItem.url.trim()) return;
    await (supabase as any).from("link_in_bio_items").insert({
      page_id: page.id,
      title: newItem.title.trim(),
      url: newItem.url.trim().startsWith("http") ? newItem.url.trim() : `https://${newItem.url.trim()}`,
      icon: newItem.icon,
      position: items.length,
      is_active: true,
    });
    setNewItem({ title: "", url: "", icon: "🔗" });
    setAddingItem(false);
    refetchItems();
  };

  const handleToggleItem = async (item: BioItem) => {
    await (supabase as any).from("link_in_bio_items").update({ is_active: !item.is_active }).eq("id", item.id);
    refetchItems();
  };

  const handleDeleteItem = async (id: string) => {
    await (supabase as any).from("link_in_bio_items").delete().eq("id", id);
    refetchItems();
  };

  const publicUrl = page ? `${window.location.origin}/bio/${page.slug}` : "";

  const copyUrl = () => {
    navigator.clipboard.writeText(publicUrl);
    toast({ title: "Link copied!" });
  };

  const effectivePage = { ...page, ...editPage } as BioPage;
  const activeItems   = items.filter(i => i.is_active);

  if (!page) {
    return (
      <div className="flex flex-col items-center justify-center py-24 space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
          <Link2 className="w-8 h-8 text-primary" />
        </div>
        <div className="text-center">
          <h2 className="text-xl font-bold">Create your Link in Bio page</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm">
            A single link that showcases all your important URLs — share it everywhere.
          </p>
        </div>
        <Button className="gradient-primary" onClick={handleCreatePage}>
          <Sparkles className="w-4 h-4 mr-2" /> Create My Page
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Link2 className="w-5 h-5 text-primary" /> Link in Bio
          </h2>
          <p className="text-sm text-muted-foreground">One link for all your content</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setPreview(p => !p)}>
            {preview ? <EyeOff className="w-3.5 h-3.5 mr-1.5" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
            {preview ? "Edit" : "Preview"}
          </Button>
          <Button size="sm" onClick={handleSavePage} disabled={saving}>
            <Check className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {/* Public URL bar */}
      <Card className="p-4">
        <div className="flex items-center gap-3">
          <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
          <span className="text-sm text-muted-foreground flex-1 truncate">{publicUrl}</span>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant={effectivePage.is_published ? "default" : "secondary"} className="text-[10px]">
              {effectivePage.is_published ? "Published" : "Draft"}
            </Badge>
            <Switch
              checked={editPage.is_published ?? !!page.is_published}
              onCheckedChange={v => setEditPage(p => ({ ...p, is_published: v }))}
            />
            <Button size="icon" variant="ghost" className="w-7 h-7" onClick={copyUrl}>
              <Copy className="w-3.5 h-3.5" />
            </Button>
            {effectivePage.is_published && (
              <Button size="icon" variant="ghost" className="w-7 h-7" asChild>
                <a href={publicUrl} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-3.5 h-3.5" /></a>
              </Button>
            )}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="space-y-4">
          <Card className="p-5 space-y-4">
            <h3 className="font-semibold text-sm">Page Settings</h3>
            <div>
              <Label className="text-xs">Page Title</Label>
              <Input value={editPage.title ?? page.title} onChange={e => setEditPage(p => ({ ...p, title: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Bio / Description</Label>
              <Textarea value={editPage.bio ?? page.bio ?? ""} onChange={e => setEditPage(p => ({ ...p, bio: e.target.value }))} rows={3} className="mt-1 resize-none" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Background</Label>
                <input type="color" value={editPage.bg_color ?? page.bg_color} onChange={e => setEditPage(p => ({ ...p, bg_color: e.target.value }))} className="mt-1 w-full h-9 rounded-md border cursor-pointer" />
              </div>
              <div>
                <Label className="text-xs">Text</Label>
                <input type="color" value={editPage.text_color ?? page.text_color} onChange={e => setEditPage(p => ({ ...p, text_color: e.target.value }))} className="mt-1 w-full h-9 rounded-md border cursor-pointer" />
              </div>
              <div>
                <Label className="text-xs">Button</Label>
                <input type="color" value={editPage.button_color ?? page.button_color} onChange={e => setEditPage(p => ({ ...p, button_color: e.target.value }))} className="mt-1 w-full h-9 rounded-md border cursor-pointer" />
              </div>
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t border-border">
              <span><Eye className="w-3.5 h-3.5 inline mr-1" />{page.view_count ?? 0} page views</span>
              <span>{items.reduce((s, i) => s + (i.click_count ?? 0), 0)} total clicks</span>
            </div>
          </Card>

          {/* Links */}
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Links ({items.length})</h3>
              <Button size="sm" variant="outline" onClick={() => setAddingItem(true)}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Link
              </Button>
            </div>

            {addingItem && (
              <div className="p-3 rounded-lg border border-primary/30 bg-primary/5 mb-3 space-y-2">
                <div className="flex gap-2">
                  <select value={newItem.icon} onChange={e => setNewItem(i => ({ ...i, icon: e.target.value }))}
                    className="border border-border rounded-md px-2 py-1.5 text-lg bg-background">
                    {ICON_OPTIONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                  </select>
                  <Input placeholder="Link title *" value={newItem.title} onChange={e => setNewItem(i => ({ ...i, title: e.target.value }))} className="flex-1" />
                </div>
                <Input placeholder="URL *" value={newItem.url} onChange={e => setNewItem(i => ({ ...i, url: e.target.value }))} />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAddItem} disabled={!newItem.title || !newItem.url}>Add</Button>
                  <Button size="sm" variant="outline" onClick={() => setAddingItem(false)}>Cancel</Button>
                </div>
              </div>
            )}

            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No links yet. Add your first link above.</p>
            ) : (
              <div className="space-y-2">
                {items.map(item => (
                  <div key={item.id} className={`flex items-center gap-2 p-2.5 rounded-lg border transition-all ${item.is_active ? "border-border" : "border-dashed border-border opacity-50"}`}>
                    <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab shrink-0" />
                    <span className="text-lg shrink-0">{item.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{item.url}</p>
                    </div>
                    <span className="text-[10px] text-muted-foreground shrink-0">{item.click_count} clicks</span>
                    <Switch checked={item.is_active} onCheckedChange={() => handleToggleItem(item)} className="shrink-0" />
                    <Button size="icon" variant="ghost" className="w-6 h-6 shrink-0 text-destructive hover:text-destructive" onClick={() => handleDeleteItem(item.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Live preview */}
        <div className="hidden lg:block">
          <Card className="overflow-hidden">
            <div className="bg-muted/50 px-4 py-2 text-xs text-muted-foreground font-medium flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5" /> Preview
            </div>
            <div className="p-4 flex justify-center">
              <div
                className="w-[280px] min-h-[480px] rounded-2xl p-6 shadow-xl flex flex-col items-center gap-3"
                style={{ background: effectivePage.bg_color, color: effectivePage.text_color }}
              >
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-2xl">
                  <Sparkles />
                </div>
                <h3 className="text-lg font-bold text-center">{effectivePage.title || "My Links"}</h3>
                {effectivePage.bio && (
                  <p className="text-xs text-center opacity-75">{effectivePage.bio}</p>
                )}
                <div className="w-full space-y-2 mt-2">
                  {activeItems.map(item => (
                    <div
                      key={item.id}
                      className="w-full py-2.5 px-4 rounded-xl flex items-center gap-3 text-sm font-medium"
                      style={{ background: effectivePage.button_color, color: "#fff" }}
                    >
                      <span>{item.icon}</span>
                      <span className="flex-1 text-center">{item.title}</span>
                    </div>
                  ))}
                  {activeItems.length === 0 && (
                    <p className="text-center text-xs opacity-50 py-4">Links will appear here</p>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
